import React from "react";
import { Box, Typography } from "@mui/material";
import CustomDialog from "../CustomDialog";
import CustomButton from "../CustomButton";
import { formatTableDate } from "../../utils/tableDateFormat";
import { ScheduleConflict } from "./types";
import { formatTime } from "./classUtils";

interface ScheduleConflictsDialogProps {
  open: boolean;
  message?: string;
  conflicts: ScheduleConflict[];
  onClose: () => void;
}

/**
 * The itemised body of a 409 from create-class or reschedule. Only the next 90
 * days are checked — a collision further out can't be predicted, so it's left
 * to first-come-first-served when someone actually books that date. That's why
 * this lists dates rather than saying "the schedule is unavailable".
 */
const ScheduleConflictsDialog: React.FC<ScheduleConflictsDialogProps> = ({
  open,
  message,
  conflicts,
  onClose,
}) => (
  <CustomDialog
    open={open}
    onClose={onClose}
    paperSx={{ minWidth: { sm: 520 }, maxWidth: 680 }}
    title="This schedule collides with existing bookings"
    actions={<CustomButton label="Back to the form" variant="outlined" onClick={onClose} />}
  >
    <Typography sx={{ fontSize: 14, mb: 2 }}>
      {message ||
        `This schedule collides with ${conflicts.length} existing booking${
          conflicts.length === 1 ? "" : "s"
        }.`}
    </Typography>

    {conflicts.length === 0 ? (
      <Typography sx={{ fontSize: 13, color: "#777" }}>
        The API did not say which dates — try a different time or day.
      </Typography>
    ) : (
      <Box>
        {conflicts.map((conflict, index) => (
          <Box
            key={`${conflict.occurrence_date || conflict.start_at || index}`}
            sx={{
              border: "1px solid #f2d0d0",
              bgcolor: "#fdf4f4",
              borderRadius: 2,
              p: 1.5,
              mb: 1,
            }}
          >
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
              {formatTableDate(conflict.start_at || conflict.occurrence_date)}
              {conflict.start_at
                ? ` · ${formatTime(conflict.start_at)}${
                    conflict.end_at ? ` - ${formatTime(conflict.end_at)}` : ""
                  }`
                : ""}
            </Typography>
            <Typography sx={{ fontSize: 13, color: "#8a3a3a" }}>
              {conflict.reason || "Already booked"}
            </Typography>
          </Box>
        ))}
      </Box>
    )}

    <Typography sx={{ fontSize: 12, color: "#777", mt: 2 }}>
      Only the next 90 days are checked. Past that, a clash is resolved when
      someone actually books the date.
    </Typography>
  </CustomDialog>
);

export default ScheduleConflictsDialog;
